import React, { useEffect, useState } from "react";
import { Grid, Checkbox, FormControlLabel, MenuItem, InputAdornment } from "@mui/material";
import AppButton from "../../../AppComponents/AppButton";
import {
  ApiContainer,
  ButtonContainerBottom,
  ButtonContainerTop,
  FormWrapper,
  ResponesBox,
  ResponseView,
  StyledInput,
  StyledLabel,
} from "../style";
import DynamicKeyValueForm from "./KeyValueInput";
import DynamicTable from "./ContactProperties";
import { useNotification } from "../../../Hooks/useNotification/useNotification";
import {
  createArrayFromObject,
  createDataFromHeadersOrParams,
  filterObject,
} from "./utils";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import JsonEditor from "./JSONInput";
import JsonViewer from "./ResponseTreeView";
import { webhookLibraryNetworkService } from "../../../Store/Slices/WebhookLibrary/webhookLibraryNetworkService";

interface Header {
  key: string;
  value: any;
}

interface WebhookCreateFormProps { }

const WebhookCreateForm: React.FC<WebhookCreateFormProps> = () => {
  const [headers, setHeaders] = useState<Header[]>([]);
  const [params, setParams] = useState<Header[]>([]);
  const [showHeaders, setShowHeaders] = useState<boolean>(false);
  const [showParams, setShowParams] = useState<boolean>(false);
  const [saveAsContact, setSaveAsContact] = useState<boolean>(false);
  const [method, setMethod] = useState<string>("POST");
  const [contactProperties, setContactProperties] = useState<any[]>([]);
  const domain: any = process.env.REACT_APP_WEBHOOK_DOMAIN
  const [webhook_domain, setWebhook_domain] = useState<string>(domain);
  const [webhookName, setWebhookName] = useState<string>("");
  const [bodyParameters, setBodyParameters] = useState<string>("{}");
  const [payloadType, setPayloadType] = useState<string>("application/json");
  const [webhookTested, setWebhookTested] = useState<boolean>(false);
  const [errors, setErrors] = useState<any>({});
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showAlert } = useNotification();
  const location = useLocation();
  const workspace_id: string = window.localStorage.getItem("ws_id") || "";
  // Reusable function to handle toggling headers or params
  const handleToggle = (
    showState: boolean,
    setShowState: React.Dispatch<React.SetStateAction<boolean>>,
    data: Header[],
    setData: React.Dispatch<React.SetStateAction<Header[]>>
  ) => {
    setShowState(!showState);
    setData(showState ? [] : [{ key: "", value: "" }]);
  };

  const handleHeadersToggle = () => {
    handleToggle(showHeaders, setShowHeaders, headers, setHeaders);
  };

  const handleParamsToggle = () => {
    handleToggle(showParams, setShowParams, params, setParams);
  };

  const handleSelectionChange = (
    nestedOption: string,
    path: string,
    isRemove: boolean
  ) => {
    let modifiedValue = path;
    const modifiedValues: string[] = [];

    if (nestedOption === "all-index" || isRemove) {
      modifiedValue = path.replace(/\.\d+/g, "[]");
      modifiedValues.push(modifiedValue);
    }

    if (nestedOption === "selected-index" || isRemove) {
      modifiedValue = path.replace(/\.(\d+)/g, "[$1]");
      modifiedValues.push(modifiedValue);
    }

    if (isRemove) {
      setContactProperties((prevSelected) =>
        prevSelected.filter(
          (item) => !modifiedValues.includes(item.api_field_name)
        )
      );
      return;
    }

    setContactProperties((prevSelected) => {
      const updatedProperties = [...prevSelected];
      modifiedValues.forEach((val) => {
        if (!updatedProperties.some((item) => item.api_field_name === val)) {
          updatedProperties.push({ api_field_name: val });
        }
      });
      return updatedProperties;
    });
  };

  useEffect(() => {
    if (headers?.length < 1) setShowHeaders(false);
  }, [headers]);

  useEffect(() => {
    if (params?.length < 1) setShowParams(false);
  }, [params]);

  useEffect(() => {
    setWebhookTested(false);
  }, [method, headers, webhookName, payloadType]);

  const handleRunTest = async () => {
    const parsedBodyParams = (() => {
      try {
        return bodyParameters?.trim()
          ? JSON.parse(bodyParameters)
          : bodyParameters;
      } catch {
        return bodyParameters;
      }
    })();

    let reqObj = {
      name: webhookName,
      method,
      header: headers || {},
      body_config: parsedBodyParams,
    };
    reqObj = filterObject(reqObj);

    if (validateRequestObject(reqObj)) return;

    // webhookLibraryNetworkService.RunTestAPI(reqObj)
    //   .then(() => {
    //     setApiTested(true);
    //     showAlert("API Tested Successfully", "success");
    //   })
    //   .catch((error) => {
    //     showAlert(
    //       error?.response?.data?.response ||
    //         "API is not working! Please check it",
    //       "error"
    //     );
    //   });
  };

  const editMapField = (type: string, index: number, value: any) => {
    setContactProperties((prev) => {
      const updatedProperties = [...prev];
      updatedProperties[index] = { ...updatedProperties[index], [type]: value };
      return updatedProperties;
    });
  };

  const validateRequestObject = (reqObj: any): boolean => {
    const errors: any = {};

    if (!reqObj.name?.trim()) {
      errors.name = "WEBHOOK Name is required.";
    }

    const validMethods = ["GET", "POST", "PUT", "DELETE"];
    if (!reqObj.method || !validMethods.includes(reqObj.method.toUpperCase())) {
      errors.method = "Valid HTTP Method is required (GET, POST, PUT, DELETE).";
    }

    if (!reqObj.payload_type?.trim()) {
      errors.payload_type = "Payload type is required.";
    }
    if (saveAsContact) {
      console.log("contactProperties", contactProperties);
    const hasEmail = contactProperties.some(
      (item) =>
        item.mapping_name?.toLowerCase().includes("email")
    );
    const hasContact = contactProperties.some(
      (item) =>
        item.mapping_name?.toLowerCase().includes("mobile_phone_number")
    );

    if (!hasEmail && !hasContact) {
      errors.contact = "At least one email or contact field is required when saving as contact.";
      showAlert(errors.contact, "error");
    }
  }else {
     errors.saveAsContact = "Save as contact is mandatory to save or update webhook";
     showAlert(errors.saveAsContact, "error");
  }
    setErrors(errors);
    return Object.keys(errors).length > 0;
  };

  const handleSubmitForm = async () => {
    // if (!apiTested) {
    //   showAlert("Please test the API before saving.", "error");
    //   return;
    // }
    let header = createDataFromHeadersOrParams(headers);
    let param = createDataFromHeadersOrParams(params);
    // let {header} = createDataFromHeaders(headers)
    const parsedBodyParams = (() => {
      try {
        return bodyParameters?.trim()
          ? JSON.parse(bodyParameters)
          : bodyParameters;
      } catch {
        return bodyParameters;
      }
    })();

    let reqObj = {
      name: webhookName,
      method,
      payload_type: payloadType,
      header: header,
      param: param,
      mapping_config: contactProperties,
      body_config: parsedBodyParams,
    };
    reqObj = filterObject(reqObj);

    if (validateRequestObject(reqObj)) return;
    try {
      if (id) {
        await webhookLibraryNetworkService.EditWebhook(id, reqObj);
        showAlert("WEBHOOK Updated Successfully", "success");
      } else {
        await webhookLibraryNetworkService.CreateWebhook(reqObj);
        showAlert("WEBHOOK Created Successfully", "success");
      }
      navigate("/webhook/library");
    } catch (error: any) {
      showAlert(
        error?.response?.data?.response || "Something Went Wrong",
        "error"
      );
    }
  };

  const handleRemoveRow = (index: number) => {
    setContactProperties((prev) => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    if (id) {
      webhookLibraryNetworkService
        .GetWebhookDetails(id)
        .then((res: any) => {
          const {
            header,
            body_config,
            method,
            name,
            mapping_config,
            store_as_contact,
          } = res?.data?.data;

          let headersData: any[] = [];
          if (header) {
            headersData = createArrayFromObject(header, []);
          }
          if (headersData?.length > 0) {
            setShowHeaders(true);
            setHeaders(headersData);
          }
          setSaveAsContact(store_as_contact);
          setBodyParameters(
            (body_config && JSON.stringify(body_config)) || "{}"
          );
          setWebhookName(name);
          setMethod(method);
          setContactProperties(mapping_config || []);
          setSaveAsContact(mapping_config?.length > 0 ? true : false);
        })
        .catch((error) => {
          showAlert(error?.message || "Something Went Wrong", "error");
          navigate("/webhook/library");
        });
    }
  }, [id]);

  return (
    <ApiContainer>
      <Grid container>
        <Grid
          item
          xs={12}
          md={6}
          style={{ overflowY: "scroll", height: "100%", background: "#fff" }}
        >
          <FormWrapper>
            <form>
              <div>
                <StyledLabel htmlFor="webhook-name">
                  Name<sup style={{ color: "red" }}>*</sup>
                </StyledLabel>
                <div>
                  <div>
                    <StyledInput
                      placeholder="Enter Webhook Name"
                      id="webhook-name"
                      fullWidth
                      margin="normal"
                      size="small"
                      value={webhookName}
                      disabled={!!id}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const value = e.target.value;
                        if (/^[a-zA-Z0-9_]*$/.test(value)) {
                          setWebhookName(value);
                        }
                      }}
                      helperText={
                        errors?.name && (
                          <span style={{ color: "red" }}>{errors?.name}</span>
                        )
                      }
                      sx={
                        errors?.name && {
                          "& .MuiOutlinedInput-root": { border: "1px solid red" },
                        }
                      }
                        style={{color: "black",
                        fontSize: "17px",
                        margin: "-8px"}}
                        InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            {webhook_domain +workspace_id + "/"}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </div>
                </div>

              </div>

              <div>
                <StyledLabel htmlFor="methods">
                  Methods<sup style={{ color: "red" }}>*</sup>
                </StyledLabel>
                <StyledInput
                  id="methods"
                  select
                  fullWidth
                  margin="normal"
                  size="small"
                  value={method}
                  disabled = {true}
                  onChange={(e: {
                    target: { value: React.SetStateAction<string> };
                  }) => setMethod(e.target.value)}
                  helperText={
                    errors?.method && (
                      <span style={{ color: "red" }}>{errors?.method}</span>
                    )
                  }
                  sx={
                    errors?.method && {
                      "& .MuiOutlinedInput-root": { border: "1px solid red" },
                    }
                  }
                >
                  {["GET", "POST", "PUT", "DELETE", "PATCH"].map((m) => (
                    <MenuItem key={m} value={m}>
                      {m}
                    </MenuItem>
                  ))}
                </StyledInput>
              </div>
              {/* 
              {["POST", "PUT", "PATCH"].includes(method) && (
                <div>
                  <StyledLabel htmlFor="payloadType">
                    Payload Type<sup style={{ color: "red" }}>*</sup>
                  </StyledLabel>
                  <StyledInput
                    id="payloadType"
                    select
                    fullWidth
                    margin="normal"
                    size="small"
                    value={payloadType}
                    onChange={(e: {
                      target: { value: React.SetStateAction<string> };
                    }) => setPayloadType(e.target.value)}
                    helperText={
                      errors?.payload_type && (
                        <span style={{ color: "red" }}>
                          {errors?.payload_type}
                        </span>
                      )
                    }
                    sx={
                      errors?.payload_type && {
                        "& .MuiOutlinedInput-root": { border: "1px solid red" },
                      }
                    }
                  >
                    <MenuItem value="application/json">JSON</MenuItem>
                    <MenuItem value="multipart/form-data">
                      Multipart form data
                    </MenuItem>
                  </StyledInput>
                </div>
              )} */}
{/* 
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showHeaders}
                    onChange={handleHeadersToggle}
                  />
                }
                label={<StyledLabel>Add Header</StyledLabel>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showParams}
                    onChange={handleParamsToggle}
                  />
                }
                label={<StyledLabel>Add Parameters</StyledLabel>}
              />
              {showHeaders && (
                <div>
                  <DynamicKeyValueForm
                    inputName="Headers"
                    data={headers}
                    setData={setHeaders}
                  />
                </div>
              )}
              {showParams && (
                <div>
                  <DynamicKeyValueForm
                    inputName="Params"
                    data={params}
                    setData={setParams}
                  />
                </div>
              )} */}
              {["POST", "PUT", "PATCH"].includes(method) &&
                payloadType === "application/json" && (
                  <div>
                    <StyledLabel htmlFor="body-parameters">
                      Enter the payload content
                    </StyledLabel>
                    <JsonEditor
                      editable={!saveAsContact}
                      onChange={setBodyParameters}
                      value={bodyParameters}
                    />
                  </div>
                )}

              {["POST", "PUT", "PATCH"].includes(method) && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={saveAsContact}
                      onChange={() => setSaveAsContact(!saveAsContact)}
                    />
                  }
                  label={<StyledLabel>Save as Contact Property</StyledLabel>}

                />
              )}

              {contactProperties.length > 0 && (
                <div>
                  <StyledLabel
                    style={{ margin: "10px 0px" }}
                    htmlFor="contact-properties"
                  >
                    Contact Properties
                  </StyledLabel>
                  <DynamicTable
                    contactProperties={contactProperties}
                    edit={true}
                    editMapField={editMapField}
                    removeRow={handleRemoveRow}
                  />
                </div>
              )}
            </form>
          </FormWrapper>
        </Grid>

        <Grid item xs={12} md={6}>
          <ResponesBox>
            <ButtonContainerTop>
              <StyledLabel>Request Body</StyledLabel>
            </ButtonContainerTop>
            <ResponseView>
              {saveAsContact ? (
                <JsonViewer
                  handleSelectionChange={handleSelectionChange}
                  jsonData={JSON.parse(bodyParameters)}
                />
              ) : (
                ""
              )}
            </ResponseView>
            <ButtonContainerBottom>
              <AppButton
                buttonStyle={{ marginRight: "5px", padding: "10px" }}
                variant="outline"
                onClick={() => navigate("/webhook/library")}
              >
                Cancel
              </AppButton>
              <AppButton
                buttonStyle={{ marginRight: "5px", padding: "10px" }}
                onClick={handleSubmitForm}
              >
                {id ? "Update" : "Save"}
              </AppButton>
            </ButtonContainerBottom>
          </ResponesBox>
        </Grid>
      </Grid>
    </ApiContainer>
  );
};

export default WebhookCreateForm;
